/**
 * web/src/components/ui/tabs.tsx
 * Radix Tabs with token-overridden styling. Used by the Confirmations page
 * (Pending / Confirmed / All tabs) and any other place where tabbed content
 * is appropriate.
 *
 * Underline-style tabs: each trigger gets a 2px bottom border that turns
 * accent indigo when active; the list itself sits on a 1px bottom border
 * so unselected tabs visually rest on the divider.
 */
import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../../lib/cn';

/** Root context provider — controls active tab. */
export const Tabs = TabsPrimitive.Root;

/**
 * Tab list container — inline-flex with a 1px bottom border that the
 * active trigger overlaps via `-mb-px` to create the underline effect.
 */
export const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn('inline-flex items-center gap-1 border-b border-border', className)}
    {...props}
  />
));
TabsList.displayName = 'TabsList';

/**
 * Trigger — 12/8 padding, 13/medium muted text by default. When active,
 * text turns text-text and the 2px bottom border turns accent indigo.
 * Hovering an inactive tab darkens its text but doesn't shift the border.
 */
export const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'px-3 py-2 text-[13px] font-medium text-text-muted border-b-2 border-transparent -mb-px',
      'data-[state=active]:text-text data-[state=active]:border-accent',
      'hover:text-text transition-colors',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = 'TabsTrigger';

/** Content panel — 16px top padding, otherwise transparent. */
export const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn('pt-4', className)} {...props} />
));
TabsContent.displayName = 'TabsContent';
